import { useState, useEffect, useMemo } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js/max';

import { DropDown, useTranslation, TextLink, TextLinkStyle, Theme, InputValidations } from '@nextgen-web-framework/all';

import {
  AlternatePhoneType,
  type DynamicObjectInterface,
  type DropDownData as Data,
  type AlternativeContactType,
} from '@commerce-profile-webcomponents/sdk';
import { parsePhoneNumber, removePhoneLeadingZero, parseString } from '../../../../utils/helper';
import PhoneNumberInput from '../../../Common/PhoneNumberInput/PhoneNumberInput';
import type { AlternativeContactProps } from '../types/propTypes';
import styles from './AlternativeContact.module.scss';

const AlternativeContact = ({ altNum, contact, fieldId, callback }: AlternativeContactProps) => {
  const t = useTranslation();

  const [contactInput, setContactInput] = useState<AlternativeContactType>(contact);
  const [isPhoneTouched, setIsPhoneTouched] = useState<boolean>(false);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);

  const labelInvalidPhone = t('profile.validationMsg.phone.format');

  const altContactType = useMemo(
    () =>
      Object.entries(AlternatePhoneType).map(([label, value]) => ({
        label: t(`profile.ProfileInfo.alternativeNumber.${label}`),
        value,
      })),
    [],
  );

  useEffect(() => {
    const { dialCode: dialCodeOri, number: numberOri } = contactInput;
    const dialCode = parseString(dialCodeOri);
    const number = parseString(numberOri);
    const isValidValue = dialCode && number ? isValidPhoneNumber(parsePhoneNumber(dialCode, number)) : false;
    setIsPhoneValid(isValidValue);

    callback?.({
      contact: {
        ...contactInput,
        isValid: isValidValue,
      },
    });
  }, [contactInput, isPhoneTouched]);

  const contactChangeHandler = (fieldId: string, value: string) => {
    setContactInput((prevContact: AlternativeContactType) => ({
      ...prevContact,
      isUpdated: true,
      [fieldId]: value,
    }));
  };

  const contactTypeChangeHandler = (item: DynamicObjectInterface) => {
    contactChangeHandler('contactType', item.value);
  };

  const removeContactHandler = (e: React.KeyboardEvent | React.MouseEvent, contact: AlternativeContactType) => {
    e.preventDefault();
    e.stopPropagation();
    callback?.({
      contact: {
        ...contact,
        isDeleted: true,
        isUpdated: false,
      },
    });
  };

  const inputChangeHandler = (number?: string) => {
    contactChangeHandler('number', removePhoneLeadingZero(parseString(number)));
  };

  const handlePhoneOnBlur = () => {
    setIsPhoneTouched(true);
  };

  const selectedContactType = parseString(altContactType.find((item: Data) => item.value === contactInput.type)?.label);

  const phoneInvalidMessage = isPhoneTouched && !isPhoneValid ? labelInvalidPhone : '';

  return (
    <div className={styles['alternative-contact-section']} data-testid="alternative-contact-testid">
      <div className={styles['divider']}></div>
      <div className={styles['alternative-header']}>
        <div className={styles['alternative-label']}>
          {t('profile.ProfileInfo.alternativeNumber')} {altNum}
        </div>
        <div>
          <TextLink
            text={t('profile.ProfileInfo.removeContact')}
            textLinkStyle={TextLinkStyle.UNDERLINE}
            theme={Theme.PURPLE}
            onClick={(e: React.KeyboardEvent | React.MouseEvent) => removeContactHandler(e, contact)}
          />
        </div>
      </div>
      <div className={styles['alternative-input']}>
        <DropDown
          name={`alternative-contactNumber-type_${altNum}`}
          data-testid="profile-contact-alt-type"
          dropdownLabel={t('profile.ProfileInfo.alternativeNumberType')}
          data={altContactType}
          configData={{
            label: 'label',
            onSelect: contactTypeChangeHandler,
          }}
          disabledFilter={true}
          value={selectedContactType}
        />

        <div>
          <PhoneNumberInput
            name={`${fieldId}-${altNum}`}
            data-testid="alternative-contact-phone-testid"
            dropdownLabel={t('profile.ProfileInfo.country')}
            dropdownValue={contactInput.dialCode}
            disabledFilter={true}
            configData={{
              label: 'name',
              onSelect: (item: DynamicObjectInterface) => {
                contactChangeHandler('dialCode', item.name);
              },
            }}
            inputLabel={t(`profile.ProfileInfo.numberOptional`)}
            inputValue={contactInput.number}
            onBlur={handlePhoneOnBlur}
            onBlurCapture={handlePhoneOnBlur}
            onInputChange={inputChangeHandler}
            isAutoValidate
            validationStatus={isPhoneTouched && !isPhoneValid ? InputValidations.INVALID : InputValidations.DEFAULT}
            validationMessage={phoneInvalidMessage}
          />
        </div>
      </div>
    </div>
  );
};

export default AlternativeContact;
